/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useTierGate } from "@/hooks/useTierGate";
import TierLockedView from "@/components/TierLockedView";
import { Clock3, ListChecks, Target, AlertTriangle } from "lucide-react";
import SEO from "../components/SEO";
import { apiService } from "../services/api.js";
import { eligibilityAsyncService } from "../services/eligibilityAsyncService.js";
import { logger } from "../utils/logger.js";
import { getResolvedDashboardEmail } from "@/lib/dashboardIdentity";
import {
  fieldAnchorId,
  focusInvalidField,
  isValidSingaporeUen,
} from "../components/checker/checkerValidation";
import { 
  CompanyProfile, 
  ProjectDetails, 
  DocumentInfo, 
  AssessmentPreferences,
  GrantOptions,
  IndustryOptions,
  CompanySizeOptions,
  BusinessStageOptions,
  CompanyTypeOptions
} from "../types/api.js";

import { isValidLatestFyStored } from "@/lib/latestFyDateInput";
import {
  getRandomSingaporePrivateDummyFormData,
  getRandomSingaporePrivateNonGovernmentDummyFormData,
  getRandomSingaporeGrantShowcaseFormData,
  getSingaporeGrantShowcaseFormData,
  SINGAPORE_PRIVATE_NON_GOVERNMENT_DUMMY_PROFILES,
} from "@/data/singaporePrivateDummyFormData.js";
import StepperProgress from "../components/checker/StepperProgress";
import { getProgress } from "../components/checker/wizard-steps";
import CompanyStep from "../components/checker/CompanyStep";
import BusinessMetricsStep from "../components/checker/BusinessMetricsStep";
import LocationOpsStep from "../components/checker/LocationOpsStep";
import GrantHistoryStep from "../components/checker/GrantHistoryStep";
import FundingStrategyStep from "../components/checker/FundingStrategyStep";
import ProjectStep, { PROJECT_DESCRIPTION_MIN_CHARS } from "../components/checker/ProjectStep";
import ComplianceStep from "../components/checker/ComplianceStep";
import ReviewStep from "../components/checker/ReviewStep";
import { useCountry } from "../contexts/CountryContext";
import Container from "../components/layout/Container";
import ThumbsFeedback from "../components/feedback/ThumbsFeedback";
import StarRatingFeedback from "../components/feedback/StarRatingFeedback";
import EnhancedFooter from "../components/EnhancedFooter";
import { GlassPanel } from "../components/ui/GlassPanel";
import { BorderBeam } from "../components/BorderBeam";
import ClientOnly from "../components/ClientOnly";
import StarsBackground from "../components/StarsBackground";
import ShootingStars from "../components/ShootingStars";
import "../styles/checker.css";

// The current eligibility route is running the Singapore flow only.
// Avoid importing the unused country-specific trees on localhost because they
// materially slow page initialization and can leave the dev screen appearing stuck.
const BNReviewStep = null;
const CountryReviewStep = null;
const MYCompanyStep = null;
const MYBusinessMetricsStep = null;
const MYTeamStep = null;
const MYProjectStep = null;
const MYEligibilityResults = null;
const IDCompanyStep = null;
const IDBusinessMetricsStep = null;
const IDTeamStep = null;
const IDProjectStep = null;
const IDEligibilityResults = null;
const THCompanyStep = null;
const THBusinessMetricsStep = null;
const THTeamStep = null;
const THProjectStep = null;
const THEligibilityResults = null;
const VNCompanyStep = null;
const VNBusinessMetricsStep = null;
const VNTeamStep = null;
const VNProjectStep = null;
const VNEligibilityResults = null;
const PHCompanyStep = null;
const PHBusinessMetricsStep = null;
const PHTeamStep = null;
const PHProjectStep = null;
const PHEligibilityResults = null;
const MMCompanyStep = null;
const MMBusinessMetricsStep = null;
const MMTeamStep = null;
const MMProjectStep = null;
const MMEligibilityResults = null;
const KHCompanyStep = null;
const KHBusinessMetricsStep = null;
const KHTeamStep = null;
const KHProjectStep = null;
const KHEligibilityResults = null;
const LACompanyStep = null;
const LABusinessMetricsStep = null;
const LATeamStep = null;
const LAProjectStep = null;
const LAEligibilityResults = null;
const BNCompanyStep = null;
const BNBusinessMetricsStep = null;
const BNTeamStep = null;
const BNProjectStep = null;
const BNEligibilityResults = null;

/** Supporting-documents wizard step is intentionally removed from this flow. */
const SG_SUPPORTING_DOCS_ENABLED = false;

const heroPillStyle = {
  color: '#60A5FA',
  backgroundColor: 'rgba(219, 234, 254, 0.45)',
  border: '1px solid rgba(147, 197, 253, 0.75)',
  boxShadow: 'none'
};

const ELIGIBILITY_HERO_PILLS = [
  { label: "Takes 5-10 minutes", icon: Clock3 },
  { label: "Section completeness", icon: ListChecks },
  { label: "Grant targeting", icon: Target },
];

/**
 * Unsaved answers live in sessionStorage for the tab's lifetime, keyed per user
 * (dashboard email) or per private-access session, so a refresh or a sidebar
 * click does not wipe a 5-10 minute form. Cleared once the assessment is submitted.
 */
const FORM_DRAFT_KEY_PREFIX = "optra_elig_draft_v1";

const readFormDraft = (key) => {
  if (!key || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && parsed.formData && typeof parsed.formData === "object"
      ? parsed.formData
      : null;
  } catch {
    return null;
  }
};

const writeFormDraft = (key, formData) => {
  if (!key || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), formData }));
  } catch {
    // storage full / unavailable: the in-memory form still works
  }
};

const clearFormDraft = (key) => {
  if (!key || typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

/** Keys the progress page or submission payload adds that must not be written back into the form. */
const PREFILL_EXCLUDED_KEYS = new Set([
  "fit_gate",
  "company_name",
  "employee_count",
  "annual_revenue",
  "project_budget",
  "project_scope",
  "singapore_based",
]);

/**
 * First-render form state: a prefill handed back by the progress page ("Try
 * again") wins, then an unsaved draft for this tab, then the defaults.
 */
const hydrateInitialFormData = (defaults, { draftKey, prefill } = {}) => {
  const merge = (source) => {
    const next = { ...defaults };
    Object.keys(source).forEach((key) => {
      if (PREFILL_EXCLUDED_KEYS.has(key)) return;
      if (!(key in defaults) && !source[key] && source[key] !== 0) return;
      next[key] = source[key];
    });
    next.declarations = { ...defaults.declarations, ...(source.declarations || {}) };
    return next;
  };
  if (prefill && typeof prefill === "object" && Object.keys(prefill).length > 0) {
    return { data: merge(prefill), restored: "prefill" };
  }
  const draft = readFormDraft(draftKey);
  if (draft) {
    return { data: merge(draft), restored: "draft" };
  }
  return { data: defaults, restored: "" };
};

/** Which section (`id="step-…"`) each validated field lives in, for the summary links. */
const SECTION_FOR_FIELD = {
  companyName: "step-company",
  uen: "step-company",
  incorporation_year: "step-company",
  industry: "step-company",
  entity_type: "step-company",
  is_group_company: "step-company",
  group_relationship: "step-company",
  latest_fy_end_date: "step-company",
  employees: "step-metrics",
  annual_revenue_sgd: "step-metrics",
  local_ownership_pct: "step-metrics",
  business_stage: "step-metrics",
  financial_viability: "step-metrics",
  cash_runway_band: "step-metrics",
  operational_presence: "step-ops",
  grant_history: "step-history",
  eligible_entity_type: "step-funding-strategy",
  funding_goal: "step-funding-strategy",
  funding_preference: "step-funding-strategy",
  revenue_status: "step-funding-strategy",
  business_model: "step-funding-strategy",
  deployment_environment: "step-funding-strategy",
  primary_use_of_funds: "step-funding-strategy",
  target_customer_type: "step-funding-strategy",
  traction_stage: "step-funding-strategy",
  market_scope: "step-funding-strategy",
  application_readiness: "step-funding-strategy",
  vc_backed: "step-funding-strategy",
  pilot_ready: "step-funding-strategy",
  impact_intent: "step-funding-strategy",
  available_documents: "step-compliance",
  financials_recency: "step-compliance",
};
/** Section id of each Singapore step in the step-by-step flow (step 1 = index 0); Review has none. */
const SG_STEP_SECTION_IDS = ['step-company', 'step-metrics', 'step-ops', 'step-history', 'step-funding-strategy', 'step-project', 'step-compliance'];

const sectionForField = (field) =>
  SECTION_FOR_FIELD[field] || (String(field).startsWith("declaration_") ? "step-compliance" : "step-project");

const SECTION_TITLES = {
  "step-company": "Company Details",
  "step-metrics": "Business Metrics",
  "step-ops": "Operational Presence",
  "step-history": "Grant History",
  "step-funding-strategy": "Funding Strategy",
  "step-project": "Project Details",
  "step-compliance": "Compliance",
};

const CTAButton = ({ children, onClick, disabled = false, iconLeft = null, iconRight = null }) => (
  <div className={`relative rounded-full ${disabled ? 'cursor-not-allowed' : ''}`}>
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="px-6 py-3 rounded-full font-medium shadow-xl hover:shadow-2xl motion-safe:hover:-translate-y-0.5 active:scale-95 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 backdrop-blur-xl bg-white/8 border border-white/20 ring-1 ring-white/10 hover:bg-white/15 flex items-center gap-2 relative z-10"
      style={{ color: '#60A5FA' }}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
    {!disabled && (
      <>
        <BorderBeam
          duration={6}
          size={400}
          borderWidth={1}
          className="from-transparent via-white to-transparent opacity-80 rounded-full"
        />
        <BorderBeam
          duration={6}
          delay={3}
          size={400}
          borderWidth={2}
          className="from-transparent via-blue-400 to-transparent opacity-60 rounded-full"
        />
      </>
    )}
  </div>
);

const sectionHeaderStyle = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontWeight: 300,
  color: '#60A5FA',
};

const CONSOLIDATED_SECTION_SUBTEXT = {
  1: "Enter your company profile and legal entity details.",
  2: "Provide workforce and financial baseline information.",
  3: "Add your operational footprint and market presence.",
  4: "Declare prior grant participation and outcomes.",
  5: "Set funding intent and strategic constraints.",
  6: "Describe project scope, timeline, and delivery plan.",
  7: "Confirm compliance readiness and governance controls.",
};

/**
 * Numbered heading for each section of the consolidated (single-page) form.
 * Module-level on purpose: defined inside render it would be a new component
 * type every render, remounting the heading and dropping keyboard focus.
 * Headings carry `#step-…-heading` so Edit / summary links can scroll to and
 * focus them; the first one is the step heading for focus management.
 */
const ConsolidatedSectionHeader = ({ index, title, sectionId }) => (
  <div className="pb-1 mb-3">
    <div className="flex items-center gap-3">
      <span
        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-sm shrink-0"
        aria-hidden
        style={{
          background: "rgba(96, 165, 250, 0.16)",
          color: "#0C447C",
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          fontWeight: 600
        }}
      >
        {index}
      </span>
      <div className="min-w-0">
        <h3
          id={`${sectionId}-heading`}
          tabIndex={-1}
          data-step-heading={index === 1 ? true : undefined}
          className="text-xl text-left focus:outline-none"
          style={sectionHeaderStyle}
        >
          <span className="sr-only">Section {index} of 7: </span>
          {title}
        </h3>
        <p
          className="mt-1 text-sm text-left"
          style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', fontWeight: 300, color: '#6B7280' }}
        >
          {CONSOLIDATED_SECTION_SUBTEXT[index]}
        </p>
      </div>
    </div>
  </div>
);

function buildEligibilitySubmissionPayload(formData) {
  // The free-text project description is the client's own definition of the
  // project. `project_objectives` stays a list of goal labels and is never used
  // as a stand-in for the description any more (that produced "projects" like
  // "Increase productivity, Enhance capabilities" in stored assessments).
  const projectDescription = String(formData.project_description || "").trim();
  const projectScope =
    projectDescription ||
    (Array.isArray(formData.project_objectives) ? formData.project_objectives.join(", ") : "") ||
    "";

  const payload = {
    ...formData,
    company_name: formData.companyName,
    employee_count: formData.employees,
    annual_revenue: formData.annual_revenue_sgd,
    project_budget: formData.project_budget_sgd,
    project_scope: projectScope,
    project_description: projectDescription,
    project_objectives: Array.isArray(formData.project_objectives) ? formData.project_objectives : [],
    singapore_based: true,
    eligible_entity_type: formData.eligible_entity_type || formData.entity_type || "",
    companyType: formData.eligible_entity_type || formData.entity_type || "",
    funding_preference: formData.funding_preference || "",
    revenue_status: formData.revenue_status || "",
    business_model: formData.business_model || "",
    deployment_environment: formData.deployment_environment || "",
    primary_use_of_funds: formData.primary_use_of_funds || "",
    target_customer_type: formData.target_customer_type || "",
    traction_stage: formData.traction_stage || "",
    market_scope: formData.market_scope || "",
    application_readiness: formData.application_readiness || "",
    vc_backed: formData.vc_backed,
    pilot_ready: formData.pilot_ready,
    impact_intent: formData.impact_intent,
  };

  // Send funding_goal only when the user actually chose one. It must never be
  // silently copied from project_type (they are different fields and the
  // backend echoes both back in the report).
  if (formData.funding_goal) {
    payload.funding_goal = formData.funding_goal;
  } else {
    delete payload.funding_goal;
  }

  return payload;
}

const DEFAULT_SG_FORM_DATA = {
  // Grant Selection
  grantType: 'psg',

  // Company Profile (Step 1)
  companyName: '',
  uen: '',
  incorporation_year: '',
  industry: '',
  company_email: '',
  contact_phone: '',
  entity_type: '',
  is_group_company: null,
  group_relationship: '',
  parent_company_name: '',
  latest_fy_end_date: '',

  // Business Metrics (Step 2)
  employees: '',
  annual_revenue_sgd: '',
  local_ownership_pct: 100,
  business_stage: '',
  financial_viability: undefined,
  cash_runway_band: '',

  // Location & Operations (Step 3)
  operational_presence: [],

  // Grant History (Step 4)
  prior_apps: [],

  // Funding Strategy (Step 5)
  eligible_entity_type: "",
  funding_goal: "",
  funding_preference: "",
  revenue_status: "",
  business_model: "",
  deployment_environment: "",
  primary_use_of_funds: "",
  target_customer_type: "",
  traction_stage: "",
  market_scope: "",
  application_readiness: "",
  vc_backed: undefined,
  pilot_ready: undefined,
  impact_intent: undefined,

  // Project Details (Step 6)
  project_type: '',
  project_maturity: '',
  expected_timeline: '',
  project_budget_sgd: '',
  project_objectives: [],
  expected_outcomes: '',
  /** Set only after user moves the co-funding slider (no default submission value). */
  cofunding_capacity: null,
  business_function_impacted: '',
  vendor_selected: null,
  vendor_country: '',
  vendor_related_party: '',
  quotation_count: '',
  can_fund_upfront: '',
  application_priority: '',
  scope_flexibility: '',
  target_market: '',
  expansion_activity_types: [],
  existing_target_market_presence: '',

  // Compliance (Step 7)
  available_documents: [],
  declarations: {
    no_outstanding_issues: undefined,
    meets_local_shareholding: undefined,
    authorized_to_apply: undefined,
    cpf_compliance_ok: undefined,
    tax_compliance_ok: undefined,
    regulatory_compliance_ok: undefined,
    prior_grant_breach: undefined,
    project_not_commenced: undefined,
    no_debarment_or_insolvency: undefined,
  },
  financials_recency: '',

  // Supporting documents (optional, Singapore — session id + metadata only in form state)
  supporting_documents_session_id: '',
  supporting_documents_consent: false,
  supporting_documents: [],

  // Assessment Preferences
  assessmentMode: 'comprehensive',
  includeAlternatives: true,
  priorityGrants: []
};

export default function CheckerPage({ embedded = false } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const clientId = location.state?.clientId || null;
  const { isLocked, requiredTier, signedIn } = useTierGate();
  // The embedded dashboard assessment must never show plan/tier gating.
  const showTierGate = !embedded && isLocked;
  const { country: _siteCountry } = useCountry();
  void _siteCountry; // Site may be multi-country; this wizard assesses Singapore government grants only.
  const isMY = false;
  const isID = false;
  const isTH = false;
  const isVN = false;
  const isPH = false;
  const isMM = false;
  const isKH = false;
  const isLA = false;
  const isBN = false;
  const isSingaporeEligibility =
    !isMY && !isID && !isTH && !isVN && !isPH && !isMM && !isKH && !isLA && !isBN;
  const isLocalhostRuntime =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "::1");
  const showLocalGrantShowcase =
    isSingaporeEligibility && isLocalhostRuntime;
  const [currentStep, setCurrentStep] = useState(1);
  const [grantHistoryDevKey, setGrantHistoryDevKey] = useState(0);
  const [results, setResults] = useState(null);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  /** Field-level issues from the last failed Continue / Run; drives inline messages and the summary. */
  const [validationIssues, setValidationIssues] = useState([]);
  const [errorsShown, setErrorsShown] = useState(false);
  /** Fit-gate "poor fit" cautions from the backend; rendered as a panel with a way through. */
  const [fitGateWarning, setFitGateWarning] = useState(null);
  const draftKey = useMemo(() => {
    const owner = getResolvedDashboardEmail() || "anon";
    return `${FORM_DRAFT_KEY_PREFIX}:${owner}`;
  }, []);
  const restoredFromRef = useRef("");
  const [formData, setFormData] = useState(() => {
    const { data, restored } = hydrateInitialFormData(DEFAULT_SG_FORM_DATA, {
      draftKey,
      prefill: location.state?.prefillFormData,
    });
    restoredFromRef.current = restored;
    return data;
  });
  const [draftNotice, setDraftNotice] = useState(() => restoredFromRef.current);
  const submittedRef = useRef(false);
  const pendingScrollSectionRef = useRef(null);
  const lastScrolledStepRef = useRef(1);
  const nonGovDemoIndexRef = useRef(0);

  // Steps sync derived keys on mount (GrantHistoryStep writes empty
  // `previous_grants*`), so ignore empty values for keys outside the defaults.
  const isDirty = useMemo(() => {
    const normalized = {};
    Object.keys(formData).forEach((key) => {
      const value = formData[key];
      if (!(key in DEFAULT_SG_FORM_DATA)) {
        const empty =
          value === '' || value === false || value === null || value === undefined ||
          (Array.isArray(value) && value.length === 0);
        if (empty) return;
      }
      normalized[key] = value;
    });
    return JSON.stringify(normalized) !== JSON.stringify(DEFAULT_SG_FORM_DATA);
  }, [formData]);

  // Persist the draft for this tab whenever answers change (and drop it once submitted).
  useEffect(() => {
    if (submittedRef.current) return;
    if (!isDirty) {
      clearFormDraft(draftKey);
      return;
    }
    writeFormDraft(draftKey, formData);
  }, [formData, isDirty, draftKey]);

  // Warn before a reload / tab close while there are unsaved answers.
  useEffect(() => {
    if (!isDirty || typeof window === "undefined") return undefined;
    const handler = (event) => {
      if (submittedRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const startOver = useCallback(() => {
    clearFormDraft(draftKey);
    setFormData(DEFAULT_SG_FORM_DATA);
    setValidationIssues([]);
    setErrorsShown(false);
    setValidationError("");
    setFitGateWarning(null);
    setDraftNotice("");
    setGrantHistoryDevKey((k) => k + 1);
    // Drop the "Try again" prefill from history state so a refresh does not bring it back.
    if (location.state?.prefillFormData) {
      navigate({ pathname: location.pathname, search: "" }, { replace: true, state: null });
    }
  }, [draftKey, location.pathname, location.state, navigate]);

  // Center and focus logic on mount (marketing page only; inside the dashboard
  // shell this auto-scrolling yanks the page around and causes scroll flicker)
  useEffect(() => {
    if (embedded) return;
    const { hash, search } = location;
    const HEADER_H = 80;
    
    const center = (el) => {
      const r = el.getBoundingClientRect();
      const y = r.top + window.pageYOffset - (window.innerHeight - el.offsetHeight) / 2;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    };
    
    const topAlign = (el) => {
      const r = el.getBoundingClientRect();
      const y = r.top + window.pageYOffset - HEADER_H - 12;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    };
    
    const targetId = hash?.slice(1);
    const knownSteps = [
      'step-company',
      'step-metrics',
      'step-ops',
      'step-history',
      'step-funding-strategy',
      'step-project',
      'step-compliance',
      ...(SG_SUPPORTING_DOCS_ENABLED && isSingaporeEligibility ? ['step-supporting-documents'] : []),
      'step-review',
    ];
    const id = knownSteps.includes(targetId) ? targetId : 'wizard-start';
    
    // Wait for DOM to be ready
    setTimeout(() => {
      const mode = new URLSearchParams(search).get('scroll');
      
      if (mode === 'top') {
        // Force scroll to top immediately - no smooth behavior
        window.scrollTo({ top: 0, behavior: 'auto' });
        // Also try document.documentElement.scrollTop as backup
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      } else {
        const el = document.getElementById('wizard-start');
        if (!el) return;
        
        const r = el.getBoundingClientRect();
        const y = r.top + window.pageYOffset - (window.innerHeight - el.offsetHeight) / 2;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }
      
      // Focus first field
      setTimeout(() => {
        const focusCandidate = document.querySelector('[name="companyName"], [data-testid="checker-card"] input');
        if (focusCandidate && typeof focusCandidate.focus === 'function') {
          focusCandidate.focus();
        }
      }, 450);
    }, 200);
  }, [embedded, isSingaporeEligibility, location]);

  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  /** Functional updates for supporting_documents without stale closures */
  const patchSupportingDocuments = useCallback((updater) => {
    setFormData((prev) => ({
      ...prev,
      supporting_documents: updater(
        Array.isArray(prev.supporting_documents) ? prev.supporting_documents : []
      ),
    }));
  }, []);

  const sgSupportingDocsStepOn = SG_SUPPORTING_DOCS_ENABLED && isSingaporeEligibility;
  // The Singapore assessment is sectioned one step per screen everywhere (the stepper, Previous /
  // Next, a Review step), the same shape as the US assessment. The consolidated single-page
  // variant is kept in the code behind this flag but no longer used (2026-09-23).
  const sgConsolidatedSectionsEnabled = false;

  const maxSteps = isBN
    ? 5
    : isMY || isID || isTH || isVN || isPH || isMM || isKH || isLA
      ? 5
      : sgSupportingDocsStepOn
        ? 9
        : 8;

  /**
   * URL helpers (embedded dashboard assessment):
      * - `?step=N` — jump to step N (e.g. 8 = supporting docs when enabled; 9 = review).
   * - `?preview=review` — demo data + pre-results review (optional `&supporting=0` for no sample files).
   * - `?preview=project` — demo data + step 6 (Project Details), including co-funding slider filled from the sample.
   */
  useEffect(() => {
    if (!embedded) return;
    const params = new URLSearchParams(location.search);
    const preview = params.get("preview");
    const previewReview = preview === "review";
    const previewProject = preview === "project";
    let shouldReplace = false;

    if (previewReview || previewProject) {
      const pick = getRandomSingaporePrivateDummyFormData();
      const supportingEmpty =
        params.get("supporting") === "0" || params.get("supporting") === "empty";
      const mockDocs =
        previewProject || !sgSupportingDocsStepOn || supportingEmpty
          ? []
          : [
              {
                document_id: "preview-doc-1",
                clientId: "preview-doc-1",
                original_filename: "BizFile_Extract_demo.pdf",
                upload_status: "complete",
              },
              {
                document_id: "preview-doc-2",
                clientId: "preview-doc-2",
                original_filename: "Vendor_quotation_sample.xlsx",
                upload_status: "complete",
              },
            ];

      setFormData((prev) => ({
        ...prev,
        ...pick,
        declarations: { ...pick.declarations },
        eligible_entity_type: pick.eligible_entity_type || prev.eligible_entity_type || "sme",
        funding_goal: pick.funding_goal || prev.funding_goal || "digital_transformation",
        funding_preference: pick.funding_preference || prev.funding_preference || "non_dilutive",
        revenue_status: pick.revenue_status || prev.revenue_status || "revenue_generating",
        business_model: pick.business_model || prev.business_model || "b2b",
        deployment_environment: pick.deployment_environment || prev.deployment_environment || "internal_operations",
        primary_use_of_funds: pick.primary_use_of_funds || prev.primary_use_of_funds || "software_tools",
        target_customer_type: pick.target_customer_type || prev.target_customer_type || "enterprise_clients",
        traction_stage: pick.traction_stage || prev.traction_stage || "paying_customers",
        market_scope: pick.market_scope || prev.market_scope || "singapore_only",
        application_readiness: pick.application_readiness || prev.application_readiness || "needs_refinement",
        vc_backed: pick.vc_backed ?? prev.vc_backed ?? false,
        pilot_ready: pick.pilot_ready ?? prev.pilot_ready ?? false,
        impact_intent: pick.impact_intent ?? prev.impact_intent ?? false,
        supporting_documents_session_id:
          prev.supporting_documents_session_id ||
          (typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `sd_${Date.now()}_preview`),
        supporting_documents: mockDocs,
        supporting_documents_consent: prev.supporting_documents_consent,
      }));
      if (previewReview) {
        setCurrentStep(maxSteps);
        setCompletedSteps(Array.from({ length: maxSteps - 1 }, (_, i) => i + 1));
      } else {
        setCurrentStep(6);
        setCompletedSteps([1, 2, 3, 4]);
      }
      params.delete("preview");
      params.delete("supporting");
      params.delete("step");
      shouldReplace = true;
    }

    const raw = params.get("step");
    if (!previewReview && !previewProject && raw != null && raw !== "") {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n) && n >= 1 && n <= maxSteps) {
        setCurrentStep(n);
        setCompletedSteps(n > 1 ? Array.from({ length: n - 1 }, (_, i) => i + 1) : []);
        params.delete("step");
        shouldReplace = true;
      }
    }

    if (!shouldReplace) return;
    const q = params.toString();
    navigate(
      { pathname: location.pathname, hash: location.hash || "", search: q ? `?${q}` : "" },
      { replace: true }
    );
  }, [
    embedded,
    location.search,
    location.pathname,
    location.hash,
    maxSteps,
    navigate,
    sgSupportingDocsStepOn,
  ]);

  useEffect(() => {
    if (!sgSupportingDocsStepOn) return;
    setFormData((prev) => {
      if (prev.supporting_documents_session_id) return prev;
      const id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `sd_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      return { ...prev, supporting_documents_session_id: id };
    });
  }, [sgSupportingDocsStepOn]);

  const fillSingaporePrivateDummy = useCallback(() => {
    const pick = getRandomSingaporePrivateDummyFormData();
    setFormData((prev) => ({
      ...prev,
      ...pick,
      declarations: { ...pick.declarations },
      eligible_entity_type: pick.eligible_entity_type || prev.eligible_entity_type || "sme",
      funding_goal: pick.funding_goal || prev.funding_goal || "digital_transformation",
      funding_preference: pick.funding_preference || prev.funding_preference || "non_dilutive",
      revenue_status: pick.revenue_status || prev.revenue_status || "revenue_generating",
      business_model: pick.business_model || prev.business_model || "b2b",
      deployment_environment: pick.deployment_environment || prev.deployment_environment || "internal_operations",
      primary_use_of_funds: pick.primary_use_of_funds || prev.primary_use_of_funds || "software_tools",
      target_customer_type: pick.target_customer_type || prev.target_customer_type || "enterprise_clients",
      traction_stage: pick.traction_stage || prev.traction_stage || "paying_customers",
      market_scope: pick.market_scope || prev.market_scope || "singapore_only",
      application_readiness: pick.application_readiness || prev.application_readiness || "needs_refinement",
      vc_backed: pick.vc_backed ?? prev.vc_backed ?? false,
      pilot_ready: pick.pilot_ready ?? prev.pilot_ready ?? false,
      impact_intent: pick.impact_intent ?? prev.impact_intent ?? false,
      supporting_documents_session_id: prev.supporting_documents_session_id,
      supporting_documents_consent: prev.supporting_documents_consent,
      supporting_documents: prev.supporting_documents,
    }));
    setValidationError('');
    setGrantHistoryDevKey((k) => k + 1);
  }, []);

  const fillSingaporePrivateNonGovernmentDummy = useCallback(() => {
    const pool = Array.isArray(SINGAPORE_PRIVATE_NON_GOVERNMENT_DUMMY_PROFILES)
      ? SINGAPORE_PRIVATE_NON_GOVERNMENT_DUMMY_PROFILES
      : [];
    const idx = pool.length > 0 ? nonGovDemoIndexRef.current % pool.length : 0;
    const base = pool.length > 0 ? pool[idx] : {};
    nonGovDemoIndexRef.current = pool.length > 0 ? (idx + 1) % pool.length : 0;
    const pick = {
      ...base,
      declarations: { ...(base.declarations || {}) },
    };
    setFormData((prev) => ({
      ...prev,
      ...pick,
      declarations: { ...pick.declarations },
      eligible_entity_type: pick.eligible_entity_type || "scaleup",
      funding_goal: pick.funding_goal || "pilot",
      funding_preference: pick.funding_preference || "blended",
      revenue_status: pick.revenue_status || "profitable",
      business_model: pick.business_model || "deeptech",
      deployment_environment: pick.deployment_environment || "sector_pilot",
      primary_use_of_funds: pick.primary_use_of_funds || "pilot_delivery",
      target_customer_type: pick.target_customer_type || "enterprise_clients",
      traction_stage: pick.traction_stage || "active_pilots",
      market_scope: pick.market_scope || "singapore_plus_asean",
      application_readiness: pick.application_readiness || "ready_to_apply",
      vc_backed: pick.vc_backed ?? true,
      pilot_ready: pick.pilot_ready ?? true,
      impact_intent: pick.impact_intent ?? true,
      dev_show_all_private_pathways: false,
      supporting_documents_session_id: prev.supporting_documents_session_id,
      supporting_documents_consent: prev.supporting_documents_consent,
      supporting_documents: prev.supporting_documents,
    }));
    setValidationError('');
    setGrantHistoryDevKey((k) => k + 1);
  }, []);

  const fillSingaporePrivateStartupDummy = useCallback(() => {
    const startupPool = Array.isArray(SINGAPORE_PRIVATE_NON_GOVERNMENT_DUMMY_PROFILES)
      ? SINGAPORE_PRIVATE_NON_GOVERNMENT_DUMMY_PROFILES.filter((profile) => {
          const stage = String(profile?.business_stage || "").toLowerCase();
          const employees = Number(profile?.employees || 0);
          return (
            stage.includes("startup") ||
            stage.includes("growth") ||
            (Number.isFinite(employees) && employees > 0 && employees <= 80)
          );
        })
      : [];
    const fallback = getRandomSingaporePrivateNonGovernmentDummyFormData();
    const pickBase =
      startupPool.length > 0
        ? startupPool[Math.floor(Math.random() * startupPool.length)]
        : fallback;
    const pick = {
      ...pickBase,
      declarations: { ...(pickBase.declarations || {}) },
    };
    setFormData((prev) => ({
      ...prev,
      ...pick,
      declarations: { ...pick.declarations },
      business_stage: pick.business_stage || "Startup",
      eligible_entity_type: pick.eligible_entity_type || "startup",
      funding_goal: pick.funding_goal || "pilot",
      funding_preference: pick.funding_preference || "non_dilutive",
      revenue_status: pick.revenue_status || "early_revenue",
      business_model: pick.business_model || "deeptech",
      deployment_environment: pick.deployment_environment || "sector_pilot",
      primary_use_of_funds: pick.primary_use_of_funds || "prototype_build",
      target_customer_type: pick.target_customer_type || "enterprise_clients",
      traction_stage: pick.traction_stage || "active_pilots",
      market_scope: pick.market_scope || "singapore_plus_asean",
      application_readiness: pick.application_readiness || "ready_to_apply",
      vc_backed: pick.vc_backed ?? true,
      pilot_ready: pick.pilot_ready ?? true,
      impact_intent: pick.impact_intent ?? true,
      dev_show_all_private_pathways: false,
      supporting_documents_session_id: prev.supporting_documents_session_id,
      supporting_documents_consent: prev.supporting_documents_consent,
      supporting_documents: prev.supporting_documents,
    }));
    setValidationError("");
    setGrantHistoryDevKey((k) => k + 1);
  }, []);

  const loadSingaporeGrantShowcase = useCallback((presetKey) => {
    const pick = getSingaporeGrantShowcaseFormData(presetKey);
    setFormData((prev) => ({
      ...prev,
      ...pick,
      declarations: { ...pick.declarations },
      eligible_entity_type: pick.eligible_entity_type || prev.eligible_entity_type || "sme",
      funding_goal: pick.funding_goal || prev.funding_goal || "digital_transformation",
      funding_preference: pick.funding_preference || prev.funding_preference || "non_dilutive",
      revenue_status: pick.revenue_status || prev.revenue_status || "revenue_generating",
      business_model: pick.business_model || prev.business_model || "b2b",
      deployment_environment: pick.deployment_environment || prev.deployment_environment || "internal_operations",
      primary_use_of_funds: pick.primary_use_of_funds || prev.primary_use_of_funds || "software_tools",
      target_customer_type: pick.target_customer_type || prev.target_customer_type || "enterprise_clients",
      traction_stage: pick.traction_stage || prev.traction_stage || "active_pilots",
      market_scope: pick.market_scope || prev.market_scope || "singapore_only",
      application_readiness: pick.application_readiness || prev.application_readiness || "ready_to_apply",
      vc_backed: pick.vc_backed ?? prev.vc_backed ?? false,
      pilot_ready: pick.pilot_ready ?? prev.pilot_ready ?? false,
      impact_intent: pick.impact_intent ?? prev.impact_intent ?? false,
      supporting_documents_session_id:
        prev.supporting_documents_session_id ||
        (typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `sd_${Date.now()}_showcase`),
      supporting_documents_consent: prev.supporting_documents_consent,
      supporting_documents: prev.supporting_documents,
    }));
    setValidationError("");
    setGrantHistoryDevKey((k) => k + 1);
    setCompletedSteps(Array.from({ length: Math.max(maxSteps - 1, 0) }, (_, i) => i + 1));
    setCurrentStep(maxSteps);
    scrollToFormTop();
  }, [maxSteps]);

  const loadRandomSingaporeGrantShowcase = useCallback(() => {
    const { data: pick } = getRandomSingaporeGrantShowcaseFormData();
    setFormData((prev) => ({
      ...prev,
      ...pick,
      declarations: { ...pick.declarations },
      eligible_entity_type: pick.eligible_entity_type || prev.eligible_entity_type || "sme",
      funding_goal: pick.funding_goal || prev.funding_goal || "digital_transformation",
      funding_preference: pick.funding_preference || prev.funding_preference || "non_dilutive",
      revenue_status: pick.revenue_status || prev.revenue_status || "revenue_generating",
      business_model: pick.business_model || prev.business_model || "b2b",
      deployment_environment: pick.deployment_environment || prev.deployment_environment || "internal_operations",
      primary_use_of_funds: pick.primary_use_of_funds || prev.primary_use_of_funds || "software_tools",
      target_customer_type: pick.target_customer_type || prev.target_customer_type || "enterprise_clients",
      traction_stage: pick.traction_stage || prev.traction_stage || "active_pilots",
      market_scope: pick.market_scope || prev.market_scope || "singapore_only",
      application_readiness: pick.application_readiness || prev.application_readiness || "ready_to_apply",
      vc_backed: pick.vc_backed ?? prev.vc_backed ?? false,
      pilot_ready: pick.pilot_ready ?? prev.pilot_ready ?? false,
      impact_intent: pick.impact_intent ?? prev.impact_intent ?? false,
      supporting_documents_session_id:
        prev.supporting_documents_session_id ||
        (typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `sd_${Date.now()}_showcase`),
      supporting_documents_consent: prev.supporting_documents_consent,
      supporting_documents: prev.supporting_documents,
    }));
    setValidationError("");
    setGrantHistoryDevKey((k) => k + 1);
    setCompletedSteps(Array.from({ length: Math.max(maxSteps - 1, 0) }, (_, i) => i + 1));
    setCurrentStep(maxSteps);
    scrollToFormTop();
  }, [maxSteps]);

  const localDevDemoPanel = showLocalGrantShowcase ? (
    <div
      className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-950"
      role="status"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">Localhost only · dev autofill presets</span>
        <span className="text-amber-900/80">
          Fill realistic answers in-place. Review answers and Run assessment still need to be clicked manually.
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="rounded-md border border-amber-600/50 bg-white/80 px-3 py-1.5 font-medium text-amber-950 hover:bg-amber-100/90 hover:!translate-y-0 hover:!shadow-none active:!translate-y-0 active:!shadow-none !min-h-0 !min-w-0 inline-flex items-center justify-center"
          onClick={fillSingaporePrivateStartupDummy}
        >
          Startup-focused dummy
        </button>
        <button
          type="button"
          className="rounded-md border border-amber-600/50 bg-white/80 px-3 py-1.5 font-medium text-amber-950 hover:bg-amber-100/90 hover:!translate-y-0 hover:!shadow-none active:!translate-y-0 active:!shadow-none !min-h-0 !min-w-0 inline-flex items-center justify-center"
          onClick={loadRandomSingaporeGrantShowcase}
        >
          Normal gov grant dummy
        </button>
        <button
          type="button"
          className="rounded-md border border-amber-600/50 bg-white/80 px-3 py-1.5 font-medium text-amber-950 hover:bg-amber-100/90 hover:!translate-y-0 hover:!shadow-none active:!translate-y-0 active:!shadow-none !min-h-0 !min-w-0 inline-flex items-center justify-center"
          onClick={fillSingaporePrivateNonGovernmentDummy}
        >
          Non-dilutive dummy
        </button>
      </div>
    </div>
  ) : null;

  const validateStep = (step) => {
    // Singapore (default) validation - 8 or 9 steps (optional supporting documents)
    if (!isMY && !isID && !isTH && !isVN && !isPH && !isMM && !isKH && !isLA && !isBN) {
      switch (step) {
        case 1: {
          const incorpOk =
            formData.incorporation_year !== '' &&
            formData.incorporation_year !== null &&
            formData.incorporation_year !== undefined;
          const groupChoiceOk = ['yes', 'no', 'not_sure'].includes(formData.is_group_company);
          return (
            formData.companyName &&
            formData.uen &&
            incorpOk &&
            formData.industry &&
            formData.entity_type &&
            groupChoiceOk &&
            isValidLatestFyStored(formData.latest_fy_end_date) &&
            (formData.is_group_company !== 'yes' || !!formData.group_relationship)
          );
        }
        case 2:
          // Check for truthy values (not empty strings) and financial_viability
          return formData.employees && 
                 formData.employees !== '' &&
                 formData.annual_revenue_sgd && 
                 formData.annual_revenue_sgd !== '' &&
                 formData.local_ownership_pct && 
                 formData.local_ownership_pct !== '' &&
                 formData.business_stage &&
                 formData.financial_viability !== undefined &&
                 !!formData.cash_runway_band;
        case 3:
          return formData.operational_presence && formData.operational_presence.length > 0;
        case 4:
          // Accept either prior_apps or the new structure (previous_grants, previous_grants_none)
          return (formData.prior_apps && formData.prior_apps.length > 0) ||
                 (formData.previous_grants && formData.previous_grants.length > 0) ||
                 (formData.previous_grants_none === true) ||
                 (formData.previous_grants_other === true);
        case 5: {
          return (
            !!formData.eligible_entity_type &&
            !!formData.funding_goal &&
            !!formData.funding_preference &&
            !!formData.revenue_status &&
            !!formData.business_model &&
            !!formData.deployment_environment &&
            !!formData.primary_use_of_funds &&
            !!formData.target_customer_type &&
            !!formData.traction_stage &&
            !!formData.market_scope &&
            !!formData.application_readiness &&
            formData.vc_backed !== undefined &&
            formData.pilot_ready !== undefined &&
            formData.impact_intent !== undefined
          );
        }
        case 6: {
          const pm = formData.project_maturity;
          // Any explicit selection counts, including "Not specified" / legacy unspecified
          const maturityOk = pm != null && String(pm).trim() !== '';
          const vendorChosen =
            formData.vendor_selected === 'Yes' || formData.vendor_selected === 'No';
          const objectivesOk =
            Array.isArray(formData.project_objectives) && formData.project_objectives.length > 0;
          const descriptionOk =
            String(formData.project_description || '').trim().length >= PROJECT_DESCRIPTION_MIN_CHARS;
          const outcomesOk = String(formData.expected_outcomes || '').trim().length > 0;
          const cf = formData.cofunding_capacity;
          const cofundingOk =
            cf !== null &&
            cf !== undefined &&
            cf !== '' &&
            Number.isFinite(Number(cf)) &&
            Number(cf) >= 0 &&
            Number(cf) <= 100;
          return (
            formData.project_type &&
            maturityOk &&
            !!formData.expected_timeline &&
            formData.project_budget_sgd &&
            formData.project_budget_sgd !== '' &&
            formData.business_function_impacted &&
            objectivesOk &&
            descriptionOk &&
            outcomesOk &&
            cofundingOk &&
            vendorChosen &&
            (formData.vendor_selected !== 'Yes' ||
              (formData.vendor_country && formData.vendor_related_party)) &&
            !!formData.quotation_count &&
            !!formData.can_fund_upfront &&
            !!formData.application_priority &&
            !!formData.scope_flexibility
          );
        }
        case 7:
          return (
            formData.available_documents &&
            formData.available_documents.length > 0 &&
            formData.declarations &&
            formData.declarations.no_outstanding_issues !== undefined &&
            formData.declarations.meets_local_shareholding !== undefined &&
            formData.declarations.authorized_to_apply !== undefined &&
            formData.declarations.cpf_compliance_ok !== undefined &&
            formData.declarations.tax_compliance_ok !== undefined &&
            formData.declarations.regulatory_compliance_ok !== undefined &&
            formData.declarations.prior_grant_breach !== undefined &&
            formData.declarations.project_not_commenced !== undefined &&
            formData.declarations.no_debarment_or_insolvency !== undefined &&
            !!formData.financials_recency
          );
        case 8: {
          if (sgSupportingDocsStepOn) {
            const pending = (formData.supporting_documents || []).some(
              (d) => d.upload_status === "uploading"
            );
            return !pending;
          }
          const validationResult = validateFormData(formData);
          return validationResult.isValid;
        }
        case 9: {
          if (!sgSupportingDocsStepOn) return false;
          const validationResult = validateFormData(formData);
          return validationResult.isValid;
        }
        default:
          return false;
      }
    } else if (isMY || isID || isTH || isVN || isPH || isMM || isKH || isLA) {
      // Other country validation (5 steps) - Updated to match actual form fields
      switch (step) {
        case 1:
          // Philippines (PH) requires: companyName, incorporation_type, company_registration, years_in_operation, filipino_ownership_pct, region, industry_sector
          if (isPH) {
            return formData.companyName && 
                   formData.incorporation_type &&
                   formData.company_registration &&
                   formData.years_in_operation !== undefined &&
                   formData.years_in_operation !== null &&
                   formData.years_in_operation !== '' &&
                   formData.filipino_ownership_pct !== undefined &&
                   formData.filipino_ownership_pct !== null &&
                   formData.filipino_ownership_pct !== '' &&
                   formData.region &&
                   formData.industry_sector;
          }
          // Vietnam (VN) requires: companyName, province, tax_code, vn_incorporated, for_profit, vietnamese_ownership_pct, industry_sector
          if (isVN) {
            return formData.companyName && 
                   formData.province &&
                   formData.tax_code &&
                   formData.tax_code !== '' &&
                   formData.vn_incorporated !== undefined &&
                   formData.vn_incorporated !== null &&
                   formData.for_profit !== undefined &&
                   formData.for_profit !== null &&
                   formData.vietnamese_ownership_pct !== undefined &&
                   formData.vietnamese_ownership_pct !== null &&
                   formData.vietnamese_ownership_pct !== '' &&
                   formData.industry_sector;
          }
          return formData.companyName && (formData.state || formData.province) && formData.industry_sector;
        case 2:
          // Cambodia (KH) requires all fields: employees, annual_revenue_khr, years_in_business, business_stage, local_ownership_pct, r_d_intensity_pct, technology_readiness_level
          if (isKH) {
            return formData.employees && 
                   formData.employees !== '' &&
                   Number(formData.employees) > 0 &&
                   formData.annual_revenue_khr && 
                   formData.annual_revenue_khr !== '' &&
                   Number(formData.annual_revenue_khr) > 0 &&
                   formData.years_in_business !== undefined && 
                   formData.years_in_business !== null && 
                   formData.years_in_business !== '' &&
                   Number(formData.years_in_business) >= 0 &&
                   formData.business_stage &&
                   formData.local_ownership_pct !== undefined && 
                   formData.local_ownership_pct !== null && 
                   formData.local_ownership_pct !== '' &&
                   Number(formData.local_ownership_pct) >= 0 &&
                   formData.r_d_intensity_pct !== undefined && 
                   formData.r_d_intensity_pct !== null && 
                   formData.r_d_intensity_pct !== '' &&
                   Number(formData.r_d_intensity_pct) >= 0 &&
                   formData.technology_readiness_level !== undefined && 
                   formData.technology_readiness_level !== null;
          }
          // Philippines (PH) requires: employees, annual_revenue_php (dropdown range), business_stage
          if (isPH) {
            return formData.employees && 
                   formData.employees !== '' &&
                   Number(formData.employees) > 0 &&
                   formData.annual_revenue_php &&
                   formData.annual_revenue_php !== '' &&
                   formData.business_stage;
          }
          // Vietnam (VN) requires: employees, annual_revenue_vnd, years_in_business, business_stage, total_capital_vnd
          if (isVN) {
            return formData.employees && 
                   formData.employees !== '' &&
                   Number(formData.employees.replace(/,/g, '')) > 0 &&
                   formData.annual_revenue_vnd && 
                   formData.annual_revenue_vnd !== '' &&
                   Number(formData.annual_revenue_vnd.replace(/,/g, '')) > 0 &&
                   formData.years_in_business !== undefined && 
                   formData.years_in_business !== null && 
                   formData.years_in_business !== '' &&
                   Number(formData.years_in_business) >= 0 &&
                   formData.business_stage &&
                   formData.total_capital_vnd &&
                   formData.total_capital_vnd !== '' &&
                   Number(formData.total_capital_vnd.replace(/,/g, '')) > 0;
          }
          // Other countries
          return formData.employees && 
                 (formData.annual_revenue_myr || formData.annual_revenue_idr || formData.annual_revenue_thb || 
                  formData.annual_revenue_vnd || formData.annual_revenue_php || formData.annual_revenue_mmk || 
                  formData.annual_revenue_khr || formData.annual_revenue_lak) && 
                 formData.business_stage;
        case 3:
          return formData.team_qualifications?.years_experience && 
                 formData.team_size &&
                 formData.has_research_partner !== undefined &&
                 formData.prior_government_funding?.length > 0 &&
                 formData.prior_private_funding?.length > 0;
        case 4:
          // Myanmar (MM) requires comprehensive project fields
          if (isMM) {
            const hasProjectTypes = Array.isArray(formData.project_types) && formData.project_types.length > 0;
            const hasExpectedOutcomes = Array.isArray(formData.expected_outcomes) && formData.expected_outcomes.length > 0;
            const hasDevelopmentStatus = formData.has_prototype === true || formData.has_mvp === true || formData.has_revenue === true;
            const hasMarketValidation = formData.has_pilot_customers === true || formData.has_letters_of_intent === true || formData.has_partnerships === true;
            
            return formData.project_description &&
                   formData.project_description.trim() !== '' &&
                   hasProjectTypes &&
                   formData.project_budget_mmk !== undefined &&
                   formData.project_budget_mmk !== null &&
                   formData.project_budget_mmk !== '' &&
                   formData.project_timeline !== undefined &&
                   formData.project_timeline !== null &&
                   formData.project_timeline !== '' &&
                   formData.co_funding_capacity_pct !== undefined &&
                   formData.co_funding_capacity_pct !== null &&
                   formData.co_funding_capacity_pct !== '' &&
                   Number(formData.co_funding_capacity_pct) >= 0 &&
                   hasExpectedOutcomes &&
                   formData.target_market_size_mmk !== undefined &&
                   formData.target_market_size_mmk !== null &&
                   formData.target_market_size_mmk !== '' &&
                   Number(formData.target_market_size_mmk) > 0 &&
                   hasDevelopmentStatus &&
                   hasMarketValidation &&
                   formData.jobs_to_create !== undefined &&
                   formData.jobs_to_create !== null &&
                   formData.jobs_to_create !== '' &&
                   Number(formData.jobs_to_create) > 0 &&
                   formData.capital_investment_planned_mmk !== undefined &&
                   formData.capital_investment_planned_mmk !== null &&
                   formData.capital_investment_planned_mmk !== '' &&
                   Number(formData.capital_investment_planned_mmk) > 0 &&
                   formData.has_export_potential !== undefined &&
                   formData.has_export_potential !== null;
          }
          // Philippines (PH) requires: project_description, project_types (array), project_budget_range, project_timeline_range
          if (isPH) {
            const hasProjectTypes = Array.isArray(formData.project_types) && formData.project_types.length > 0;
            return formData.project_description &&
                   formData.project_description.trim() !== '' &&
                   hasProjectTypes &&
                   formData.project_budget_range &&
                   formData.project_budget_range !== '' &&
                   formData.project_timeline_range &&
                   formData.project_timeline_range !== '';
          }
          // Other countries
          return formData.project_description &&
                 formData.project_description.trim() !== '' &&
                 (formData.project_budget_myr || formData.project_budget_idr || formData.project_budget_thb || 
                  formData.project_budget_vnd || formData.project_budget_php || formData.project_budget_mmk || 
                  formData.project_budget_khr || formData.project_budget_lak) &&
                 (formData.project_timeline || formData.project_timeline_months);
        case 5: {
          // Review step - always valid if we reached it (all previous steps validated)
          return true;
        }
        default:
          return false;
      }
    } else if (isBN) {
      // Brunei validation - ALL fields must be filled
      switch (step) {
        case 1:
          // All fields: companyName, state, city, incorporation_type, incorporation_year, company_registration, industry_sector, bn_incorporated, for_profit
          return formData.companyName && 
                 formData.state && 
                 formData.city && 
                 formData.incorporation_type && 
                 formData.incorporation_year && 
                 formData.company_registration && 
                 formData.industry_sector && 
                 formData.bn_incorporated !== undefined && 
                 formData.bn_incorporated !== null && 
                 formData.for_profit !== undefined && 
                 formData.for_profit !== null;
        case 2:
          // Required fields: employees, annual_revenue_bnd, years_in_business, business_stage, r_d_intensity_pct, technology_readiness_level
          // Checklist items (has_revenue, has_funding, has_patents, has_trademarks, has_other_ip) are optional
          // Note: employees and annual_revenue_bnd are stored as strings from number formatting, so we parse for comparison
          return formData.employees !== undefined && 
                 formData.employees !== null && 
                 formData.employees !== '' &&
                 Number(formData.employees) > 0 && 
                 formData.annual_revenue_bnd !== undefined && 
                 formData.annual_revenue_bnd !== null && 
                 formData.annual_revenue_bnd !== '' &&
                 Number(formData.annual_revenue_bnd) > 0 &&
                 formData.years_in_business !== undefined && 
                 formData.years_in_business !== null && 
                 formData.business_stage && 
                 formData.r_d_intensity_pct !== undefined && 
                 formData.r_d_intensity_pct !== null && 
                 formData.technology_readiness_level !== undefined && 
                 formData.technology_readiness_level !== null;
        case 3: {
          // All fields: team_qualifications (years_experience, at least one qualification checked), team_size, has_research_partner, partner_is_university/partner_is_research_institution (if has_research_partner is true), prior_government_funding, prior_private_funding
          const hasAtLeastOneQualification =
            formData.team_qualifications?.has_technical_degree === true ||
            formData.team_qualifications?.has_business_degree === true ||
            formData.team_qualifications?.has_industry_experience === true;

          return formData.team_qualifications?.years_experience !== undefined &&
                 formData.team_qualifications?.years_experience !== null &&
                 formData.team_qualifications?.years_experience > 0 &&
                 hasAtLeastOneQualification &&
                 formData.team_size !== undefined &&
                 formData.team_size !== null &&
                 formData.team_size !== '' &&
                 Number(formData.team_size) > 0 &&
                 formData.has_research_partner !== undefined &&
                 (formData.has_research_partner === false ||
                  (formData.has_research_partner === true &&
                   (formData.partner_is_university === true || formData.partner_is_research_institution === true))) &&
                 Array.isArray(formData.prior_government_funding) &&
                 formData.prior_government_funding.length > 0 &&
                 Array.isArray(formData.prior_private_funding) &&
                 formData.prior_private_funding.length > 0;
        }
        case 4: {
          // Required fields: project_description, project_types (at least one), project_budget_bnd, project_timeline, co_funding_capacity_pct, expected_outcomes (at least one), target_market_size_bnd
          const hasProjectTypes = Array.isArray(formData.project_types) && formData.project_types.length > 0;
          const hasExpectedOutcomes = Array.isArray(formData.expected_outcomes) && formData.expected_outcomes.length > 0;

          return formData.project_description && 
                 formData.project_description.trim() !== '' &&
                 hasProjectTypes &&
                 formData.project_budget_bnd !== undefined && 
                 formData.project_budget_bnd !== null && 
                 formData.project_budget_bnd !== '' &&
                 Number(formData.project_budget_bnd) > 0 &&
                 formData.project_timeline !== undefined && 
                 formData.project_timeline !== null && 
                 formData.project_timeline !== '' &&
                 formData.co_funding_capacity_pct !== undefined && 
                 formData.co_funding_capacity_pct !== null && 
                 formData.co_funding_capacity_pct !== '' &&
                 Number(formData.co_funding_capacity_pct) >= 0 &&
                 hasExpectedOutcomes &&
                 formData.target_market_size_bnd !== undefined && 
                 formData.target_market_size_bnd !== null && 
                 formData.target_market_size_bnd !== '' &&
                 Number(formData.target_market_size_bnd) > 0;
        }
        case 5: {
          // Review step - always valid if we reached it (all previous steps validated)
          return true;
        }
        default:
          return false;
      }
    }
    return false;
  };

  const collectValidationIssues = (data) => {
    const issues = [];
    const addIssue = (field, message, label = null) => {
      issues.push({ field, message, label: label || field });
    };
    
    if (isBN) {
      // Brunei validation
      if (!data.companyName) addIssue('companyName', 'Company name is required', 'Company name');
      if (!data.state) addIssue('state', 'District is required', 'District');
      if (!data.industry_sector) addIssue('industry_sector', 'Industry sector is required', 'Industry sector');
      if (!data.employees) addIssue('employees', 'Employee count is required', 'Employee count');
      if (!data.annual_revenue_bnd) addIssue('annual_revenue_bnd', 'Annual revenue is required', 'Annual revenue');
      if (!data.project_description) addIssue('project_description', 'Project description is required', 'Project description');
      if (!data.project_budget_bnd) addIssue('project_budget_bnd', 'Project budget is required', 'Project budget');
    } else if (isMY || isID || isTH || isVN || isPH || isMM || isKH || isLA) {
      // Other country validation (similar to Brunei but may have different fields)
      if (!data.companyName) addIssue('companyName', 'Company name is required', 'Company name');
      if (!data.employees) addIssue('employees', 'Employee count is required', 'Employee count');
      if (!data.annual_revenue_sgd) addIssue('annual_revenue_sgd', 'Annual revenue is required', 'Annual revenue');
    } else {
      // Singapore validation. Labels match the visible field labels so the
      // summary and inline messages name the same thing the user sees.
      if (!data.companyName) addIssue('companyName', 'Enter your company name', 'Company Name');
      if (!data.uen) {
        addIssue('uen', 'Enter your UEN as shown on ACRA BizFile', 'UEN');
      } else if (!isValidSingaporeUen(data.uen)) {
        addIssue('uen', 'Enter a valid UEN, e.g. 202012345A or 53012345A (9 or 10 characters ending in a letter)', 'UEN');
      }
      if (
        data.incorporation_year === '' ||
        data.incorporation_year === null ||
        data.incorporation_year === undefined
      ) {
        addIssue('incorporation_year', 'Enter the year of incorporation', 'Year of Incorporation');
      }
      if (!data.industry) addIssue('industry', 'Select your primary industry', 'Primary Industry');
      if (!data.entity_type) addIssue('entity_type', 'Select your business entity type', 'Business Entity Type');
      if (!['yes', 'no', 'not_sure'].includes(data.is_group_company)) {
        addIssue('is_group_company', 'Choose whether the company is part of a group or owned by a parent company', 'Group / Parent Company Relationship');
      }
      if (data.is_group_company === 'yes' && !data.group_relationship) {
        addIssue('group_relationship', 'Select the group / parent company relationship', 'Relationship Details');
      }
      if (!data.latest_fy_end_date) {
        addIssue('latest_fy_end_date', 'Enter the latest completed financial year end as dd/mm/yyyy', 'Latest Completed Financial Year / Audited Period End');
      } else if (!isValidLatestFyStored(data.latest_fy_end_date)) {
        addIssue('latest_fy_end_date', 'Enter the latest financial year end as dd/mm/yyyy (e.g. 31/12/2024)', 'Latest Completed Financial Year / Audited Period End');
      }
      if (!data.employees) addIssue('employees', 'Enter the number of employees', 'Number of Employees');
      if (!data.annual_revenue_sgd) addIssue('annual_revenue_sgd', 'Enter your annual revenue in SGD', 'Annual Revenue (SGD)');
      if (!data.local_ownership_pct && data.local_ownership_pct !== 0) {
        addIssue('local_ownership_pct', 'Enter the local ownership percentage', 'Local Ownership Percentage');
      }
      if (!data.business_stage) addIssue('business_stage', 'Select your business stage', 'Business Stage');
      if (data.financial_viability === undefined || data.financial_viability === null) {
        addIssue('financial_viability', 'Choose whether the company can co-fund the project', 'Financial Viability');
      }
      if (!data.cash_runway_band) addIssue('cash_runway_band', 'Select your operating cash runway', 'Operating Cash Runway');
      if (!Array.isArray(data.operational_presence) || data.operational_presence.length === 0) {
        addIssue('operational_presence', 'Select at least one operational presence option', 'Operational Presence');
      }
      const grantHistoryOk =
        (data.prior_apps && data.prior_apps.length > 0) ||
        (data.previous_grants && data.previous_grants.length > 0) ||
        data.previous_grants_none === true ||
        data.previous_grants_other === true;
      if (!grantHistoryOk) {
        addIssue('grant_history', 'Select the grants you have received or applied for, or “None previously received”', 'Previous Grant Experience');
      }
      if (!data.eligible_entity_type) addIssue('eligible_entity_type', 'Select your company category', 'Company category');
      if (!data.funding_goal) addIssue('funding_goal', 'Select your primary funding goal', 'Primary Funding Goal');
      if (!data.funding_preference) addIssue('funding_preference', 'Select your capital preference', 'Capital Preference');
      if (!data.revenue_status) addIssue('revenue_status', 'Select your revenue status', 'Revenue Status');
      if (!data.business_model) addIssue('business_model', 'Select your business model', 'Business Model');
      if (!data.deployment_environment) addIssue('deployment_environment', 'Select the deployment environment', 'Deployment Environment');
      if (!data.primary_use_of_funds) addIssue('primary_use_of_funds', 'Select the primary use of funds', 'Primary Use Of Funds');
      if (!data.target_customer_type) addIssue('target_customer_type', 'Select your target buyer / customer', 'Target Buyer / Customer');
      if (!data.traction_stage) addIssue('traction_stage', 'Select your current traction', 'Current Traction');
      if (!data.market_scope) addIssue('market_scope', 'Select your market scope', 'Market Scope');
      if (!data.application_readiness) addIssue('application_readiness', 'Select your application readiness', 'Application Readiness');
      if (data.vc_backed === undefined || data.vc_backed === null) {
        addIssue('vc_backed', 'Choose whether the company is VC-backed', 'VC-backed status');
      }
      if (data.pilot_ready === undefined || data.pilot_ready === null) {
        addIssue('pilot_ready', 'Choose whether the company is pilot-ready', 'Pilot readiness');
      }
      if (data.impact_intent === undefined || data.impact_intent === null) {
        addIssue('impact_intent', 'Choose whether the company is impact-driven or commercial-first', 'Impact Intent');
      }
      if (!data.project_type) addIssue('project_type', 'Select the project type', 'Project Type');
      const pm = data.project_maturity;
      const maturityOk = pm != null && String(pm).trim() !== '';
      if (!maturityOk) {
        addIssue('project_maturity', 'Select the project stage (or “Not specified”)', 'Project stage');
      }
      if (!data.expected_timeline) addIssue('expected_timeline', 'Select the expected timeline', 'Expected Timeline');
      if (!data.project_budget_sgd) addIssue('project_budget_sgd', 'Enter the project budget in SGD', 'Project Budget (SGD)');
      if (!data.business_function_impacted) {
        addIssue('business_function_impacted', 'Select the business function this project impacts most', 'Business Function Impacted');
      }
      if (!Array.isArray(data.project_objectives) || data.project_objectives.length === 0) {
        addIssue('project_objectives', 'Add at least one project objective', 'Project Objectives');
      }
      if (String(data.project_description || '').trim().length < PROJECT_DESCRIPTION_MIN_CHARS) {
        addIssue(
          'project_description',
          `Describe the project in at least ${PROJECT_DESCRIPTION_MIN_CHARS} characters (what you will build or do, for whom, by when)`,
          'Describe the project'
        );
      }
      if (!String(data.expected_outcomes || '').trim()) {
        addIssue('expected_outcomes', 'Describe the expected outcomes', 'Expected Outcomes');
      }
      {
        const cf = data.cofunding_capacity;
        const cofundingOk =
          cf !== null &&
          cf !== undefined &&
          cf !== '' &&
          Number.isFinite(Number(cf)) &&
          Number(cf) >= 0 &&
          Number(cf) <= 100;
        if (!cofundingOk) {
          addIssue('cofunding_capacity', 'Set your co-funding capacity with the slider or a quick pick', 'Co-funding Capacity');
        }
      }
      if (data.vendor_selected !== 'Yes' && data.vendor_selected !== 'No') {
        addIssue('vendor_selected', 'Choose whether you have selected a vendor', 'Have you selected a vendor for this project?');
      }
      if (data.vendor_selected === 'Yes') {
        if (!data.vendor_country) {
          addIssue('vendor_country', 'Select where the vendor is based', 'Where is the vendor based?');
        }
        if (!data.vendor_related_party) {
          addIssue('vendor_related_party', 'Choose whether the vendor is related to your company', 'Is the vendor related to your company, directors, or shareholders?');
        }
      }
      if (!data.quotation_count) {
        addIssue('quotation_count', 'Select how many vendor quotations you have', 'Number of vendor quotations');
      }
      if (!data.can_fund_upfront) {
        addIssue('can_fund_upfront', 'Choose whether the company can pay upfront before reimbursement', 'Can your company pay upfront for the project before reimbursement?');
      }
      if (!data.application_priority) {
        addIssue('application_priority', 'Select what matters most for this application', 'What matters most for this application?');
      }
      if (!data.scope_flexibility) {
        addIssue('scope_flexibility', 'Choose whether you could adjust the project scope or structure', 'Would you be open to adjusting the project scope or structure to improve eligibility?');
      }
      if (!data.available_documents?.length) {
        addIssue('available_documents', 'Select the documents you have, or “None of these yet”', 'Available Documents');
      }
      const dec = data.declarations || {};
      [
        ['no_outstanding_issues', 'Does your company have any outstanding issues with a government agency?'],
        ['meets_local_shareholding', 'Is at least 30% of the company owned by Singapore citizens or permanent residents?'],
        ['authorized_to_apply', 'Are you authorised to submit grant applications for this company?'],
        ['cpf_compliance_ok', 'Are there any outstanding CPF contributions or CPF-related issues?'],
        ['tax_compliance_ok', 'Are there any outstanding tax filings, tax payments or IRAS disputes?'],
        ['regulatory_compliance_ok', 'Is the company facing any ongoing legal, regulatory or compliance action?'],
        ['prior_grant_breach', 'Has the company ever defaulted on, breached the terms of, or had a clawback on a government grant?'],
        ['project_not_commenced', 'Has the project already started, or has any payment, deposit or contract for it been committed?'],
        ['no_debarment_or_insolvency', 'Has the company or any of its directors been debarred, made insolvent, or been subject to winding-up?'],
      ].forEach(([key, question]) => {
        if (dec[key] === undefined || dec[key] === null) {
          addIssue(`declaration_${key}`, 'Answer Yes or No', question);
        }
      });
      if (!data.financials_recency) {
        addIssue('financials_recency', 'Select how recent your financial statements are', 'Document Recency');
      }
    }

    return issues;
  };

  const summarizeValidationIssues = (issues) => {
    if (!issues.length) return 'Please complete the required field before continuing.';

    const labels = [...new Set(issues.map((issue) => issue.label).filter(Boolean))];
    if (!labels.length) return 'Please complete the required field before continuing.';

    if (labels.length === 1) {
      return `Please complete "${labels[0]}" before continuing.`;
    }

    if (labels.length === 2) {
      return `Please complete "${labels[0]}" and "${labels[1]}" before continuing.`;
    }

    const visibleLabels = labels.slice(0, 3).map((label) => `"${label}"`);
    const remainingCount = labels.length - visibleLabels.length;
    const listedFields = `${visibleLabels.slice(0, -1).join(', ')}, and ${visibleLabels[visibleLabels.length - 1]}`;

    return remainingCount > 0
      ? `Please complete ${listedFields} before continuing. ${remainingCount} more required field${remainingCount === 1 ? '' : 's'} still need attention.`
      : `Please complete ${listedFields} before continuing.`;
  };

  const validateFormData = (data) => {
    const issues = collectValidationIssues(data);

    return {
      isValid: issues.length === 0,
      errors: issues.map((issue) => issue.message),
      issues,
    };
  };

  /** field -> message for the steps' inline messages (only after a failed Continue / Run). */
  const fieldErrors = useMemo(() => {
    if (!errorsShown) return {};
    const map = {};
    validationIssues.forEach((issue) => {
      if (!map[issue.field]) map[issue.field] = issue.message;
    });
    return map;
  }, [validationIssues, errorsShown]);

  /**
   * The answers still missing, limited to the step on screen in the step-by-step Singapore flow
   * (the other sections are not visible there); every section in the consolidated flow.
   */
  const collectIssuesForStep = (data, step) => {
    const issues = collectValidationIssues(data);
    const stepSection = SG_STEP_SECTION_IDS[step - 1];
    if (sgConsolidatedSectionsEnabled || !isSingaporeEligibility || !stepSection) return issues;
    return issues.filter((issue) => sectionForField(issue.field) === stepSection);
  };

  // Once errors are on screen, re-validate as the user fixes answers so messages clear.
  useEffect(() => {
    if (!errorsShown) return;
    setValidationIssues(collectIssuesForStep(formData, currentStep));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, errorsShown, currentStep]);

  const showValidationIssues = (issues) => {
    setValidationIssues(issues);
    setErrorsShown(true);
    setValidationError('');
    if (issues.length > 0 && typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        if (!focusInvalidField(issues[0].field)) {
          const section = document.getElementById(sectionForField(issues[0].field));
          section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  };

  const clearValidationIssues = () => {
    setValidationIssues([]);
    setErrorsShown(false);
    setValidationError('');
  };

  const scrollToFormTop = () => {
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      const target = document.getElementById('wizard-start');
      if (target) {
        const headerOffset = 120;
        const rect = target.getBoundingClientRect();
        const offset = rect.top + window.pageYOffset - headerOffset;
        window.scrollTo({ top: Math.max(offset, 0), behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  /** Move focus to the visible step heading so assistive tech announces the new screen. */
  const focusStepHeading = () => {
    if (typeof document === 'undefined') return;
    const card = document.getElementById('wizard-start') || document;
    const headings = Array.from(card.querySelectorAll('[data-step-heading]'));
    const visible = headings.find((el) => el.offsetParent !== null);
    if (visible && typeof visible.focus === 'function') {
      try {
        visible.focus({ preventScroll: true });
      } catch {
        visible.focus();
      }
    }
  };

  /** Scroll to a section of the consolidated form (`#step-…`) and focus its heading. */
  const scrollToSection = (sectionId) => {
    if (typeof document === 'undefined' || !sectionId) return false;
    const section = document.getElementById(sectionId);
    if (!section) return false;
    const heading = document.getElementById(`${sectionId}-heading`) || section;
    const headerOffset = 120;
    const rect = heading.getBoundingClientRect();
    window.scrollTo({ top: Math.max(rect.top + window.pageYOffset - headerOffset, 0), behavior: 'smooth' });
    if (typeof heading.focus === 'function') {
      try {
        heading.focus({ preventScroll: true });
      } catch {
        heading.focus();
      }
    }
    return true;
  };

  const handleConsolidatedContinue = () => {
    const validationResult = validateFormData(formData);

    if (!validationResult.isValid) {
      showValidationIssues(validationResult.issues);
      return;
    }

    clearValidationIssues();
    setCurrentStep(8);
    scrollToFormTop();
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      clearValidationIssues();
      setCompletedSteps(prev => [...prev, currentStep]);
      setCurrentStep((prev) => Math.min(prev + 1, maxSteps));
      // Scroll to form top smoothly
      scrollToFormTop();
    } else {
      // Mark the invalid fields inline as well as the step-level message below. In the step-by-step
      // flow only the current step's answers are listed: the other sections are not on screen.
      const stepIssues = collectIssuesForStep(formData, currentStep);
      setValidationIssues(stepIssues);
      setErrorsShown(true);
      // Put the cursor in the first missing answer, as the consolidated flow did.
      if (stepIssues.length > 0 && typeof window !== 'undefined') {
        window.requestAnimationFrame(() => focusInvalidField(stepIssues[0].field));
      }
      // Set validation error message based on current step
      const errorMessages = {
        1: isBN 
          ? 'Please fill in all company information fields (Company Name, District, City, Incorporation Type, Year of Incorporation, Business Registration Number, Industry Sector, Brunei Incorporated, and Business Type).'
          : isVN
          ? 'Please fill in all required company information (Company Name, Province, Tax Code, Vietnamese Incorporated, Business Type, Vietnamese Ownership %, and Industry Sector).'
          : 'Please complete all required company fields: name, UEN, year of incorporation, industry, entity type, group structure, and latest financial year end.',
        2: isBN
          ? 'Please fill in all required business metrics fields (Employees, Annual Revenue, Years in Business, Business Stage, R&D Intensity, and Technology Readiness Level).'
          : isKH
          ? 'Please fill in all required fields: Number of Employees, Annual Revenue (KHR), Years in Business, Business Stage, Local Ownership (%), R&D Intensity (%), and Technology Readiness Level (TRL).'
          : isVN
          ? 'Please fill in all required business metrics fields (Number of Employees, Annual Revenue (VND), Years in Business, Business Stage, and Total Capital/Assets (VND)).'
          : 'Please complete business metrics: employees, revenue, local ownership, stage, financial viability, and operating cash runway.',
        3: isBN
          ? 'Please fill in all team and qualifications fields (Team experience, qualifications, team size, research partnerships, and prior funding history).'
          : 'Please select at least one operational presence location.',
        4: isBN
          ? 'Please fill in all project and commercialization fields (Project description, budget, timeline, market size, development status, market validation, economic impact, and export potential).'
          : (isMY || isID || isTH || isVN || isPH || isMM || isKH || isLA)
          ? 'Please fill in all project and commercialization fields (Project description, budget, timeline, market size, development status, market validation, economic impact, and export potential).'
          : 'Please indicate your grant history by selecting at least one option.',
        5: isBN || isMY || isID || isTH || isVN || isPH || isMM || isKH || isLA
          ? 'Please review all your information before submitting.'
          : 'Please complete the funding strategy section: entity type, funding goal, capital preference, revenue status, business model, and readiness indicators.',
        6: isBN || isMY || isID || isTH || isVN || isPH || isMM || isKH || isLA
          ? 'Please review all your information before submitting.'
          : 'Please complete project details: type, stage, timeline, budget, objectives, expected outcomes, vendor and quotations, and funding priorities.',
        7: 'Please select available documents and complete all declarations.',
        8: sgSupportingDocsStepOn
          ? 'Please wait for uploads to finish, or remove any files that failed.'
          : 'Please review and ensure all required information is complete.',
        9: 'Please review and ensure all required information is complete.'
      };
      setValidationError(errorMessages[currentStep] || 'Please complete all fields before proceeding.');
    }
  };

  const handlePrevious = () => {
    setValidationError(''); // Clear errors when going back
    setFitGateWarning(null);
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Scroll to form top smoothly
    scrollToFormTop();
  };

  /**
   * Review "Edit": in consolidated mode every section lives on step 1, so land
   * on the requested section (`sectionId`, e.g. `step-project`) rather than the
   * top of the whole form. In the step-by-step (?step=N) flow, go to that step.
   */
  const handleEditStep = (stepNumber, sectionId = null) => {
    setValidationError(''); // Clear errors when editing a step
    setFitGateWarning(null);
    const targetStep = sgConsolidatedSectionsEnabled && stepNumber >= 1 && stepNumber <= 7 ? 1 : stepNumber;
    if (targetStep === currentStep && sectionId) {
      scrollToSection(sectionId);
      return;
    }
    pendingScrollSectionRef.current = sgConsolidatedSectionsEnabled ? sectionId : null;
    setCurrentStep(targetStep);
  };

  const handleEditSection = (sectionId) => handleEditStep(1, sectionId);

  useEffect(() => {
    // Only a real step change scrolls and moves focus. A "mounted" flag would misfire under React
    // StrictMode (effects run twice on mount in development) and yank the page down on open.
    if (lastScrolledStepRef.current === currentStep) return;
    lastScrolledStepRef.current = currentStep;
    const pendingSection = pendingScrollSectionRef.current;
    pendingScrollSectionRef.current = null;
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      if (pendingSection && scrollToSection(pendingSection)) return;
      scrollToFormTop();
      focusStepHeading();
    });
  }, [currentStep]);


  const handleSubmit = async (options = {}) => {
    const skipFitGate = options && options.skipFitGate === true;
    if (isSubmitting) return;
    setIsSubmitting(true);
    logger.debug('[checker] Run assessment clicked (SG async flow)');

    try {
        // Validate form before submission
        const validationResult = validateFormData(formData);
        if (!validationResult.isValid) {
          logger.debug('[checker] Validation failed:', validationResult.errors);
          showValidationIssues(validationResult.issues);
          return;
        }
        clearValidationIssues();

        const submissionPayload = buildEligibilitySubmissionPayload(formData);
        let fitGateResult = null;
        try {
          fitGateResult = await apiService.assessmentFitGate(submissionPayload);
          const fitGate = fitGateResult?.fit_gate || {};
          if (fitGate.status === 'poor_fit_warning' && !skipFitGate) {
            const cautions = Array.isArray(fitGate.cautions) ? fitGate.cautions.filter(Boolean) : [];
            setFitGateWarning({
              cautions:
                cautions.length > 0
                  ? cautions
                  : ['This project does not strongly match the current Singapore SME grant assessment scope.'],
            });
            return;
          }
        } catch (fitGateError) {
          logger.warn('[checker] Fit gate unavailable, continuing with assessment submission:', fitGateError);
        }
        setFitGateWarning(null);

        // Navigate IMMEDIATELY to loading page (no delay)
        const tempTraceId = `trace_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        logger.debug(`[TRACE-${tempTraceId}] Navigating to the progress page`);

        submittedRef.current = true;
        clearFormDraft(draftKey);
        navigate(`/dashboard/assessment-progress?pending=true`, {
          state: {
            jobId: 'creating',
            traceId: tempTraceId,
            formData: {
              ...submissionPayload,
              fit_gate: fitGateResult?.fit_gate || null,
            },
            startedAt: new Date().toISOString(),
            createJobInBackground: true,
            clientId,
          },
          replace: true
        });
    } catch (error) {
      logger.error('[checker] Submission failed:', error);
      submittedRef.current = false;
      setValidationError(error.message || 'Unable to process assessment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    // MY flows have 4 steps, SG has 7 steps
    if (isMY) {
      switch (currentStep) {
        case 1:
          return <MYCompanyStep formData={formData} updateFormData={updateFormData} />;
        case 2:
          return <MYBusinessMetricsStep formData={formData} updateFormData={updateFormData} />;
        case 3:
          return <MYTeamStep formData={formData} updateFormData={updateFormData} />;
        case 4:
          return <MYProjectStep formData={formData} updateFormData={updateFormData} />;
        case 5:
          return <CountryReviewStep formData={formData} updateFormData={updateFormData} onEdit={handleEditStep} onSubmit={handleSubmit} />;
        default:
          return null;
      }
    } else if (isID) {
      switch (currentStep) {
        case 1:
          return <IDCompanyStep formData={formData} updateFormData={updateFormData} />;
        case 2:
          return <IDBusinessMetricsStep formData={formData} updateFormData={updateFormData} />;
        case 3:
          return <IDTeamStep formData={formData} updateFormData={updateFormData} />;
        case 4:
          return <IDProjectStep formData={formData} updateFormData={updateFormData} />;
        case 5:
          return <CountryReviewStep formData={formData} updateFormData={updateFormData} onEdit={handleEditStep} onSubmit={handleSubmit} />;
        default:
          return null;
      }
    } else if (isTH) {
      switch (currentStep) {
        case 1:
          return <THCompanyStep formData={formData} updateFormData={updateFormData} />;
        case 2:
          return <THBusinessMetricsStep formData={formData} updateFormData={updateFormData} />;
        case 3:
          return <THTeamStep formData={formData} updateFormData={updateFormData} />;
        case 4:
          return <THProjectStep formData={formData} updateFormData={updateFormData} />;
        case 5:
          return <CountryReviewStep formData={formData} updateFormData={updateFormData} onEdit={handleEditStep} onSubmit={handleSubmit} />;
        default:
          return null;
      }
    } else if (isVN) {
      switch (currentStep) {
        case 1:
          return <VNCompanyStep formData={formData} updateFormData={updateFormData} />;
        case 2:
          return <VNBusinessMetricsStep formData={formData} updateFormData={updateFormData} />;
        case 3:
          return <VNTeamStep formData={formData} updateFormData={updateFormData} />;
        case 4:
          return <VNProjectStep formData={formData} updateFormData={updateFormData} />;
        case 5:
          return <CountryReviewStep formData={formData} updateFormData={updateFormData} onEdit={handleEditStep} onSubmit={handleSubmit} />;
        default:
          return null;
      }
    } else if (isPH) {
      switch (currentStep) {
        case 1:
          return <PHCompanyStep formData={formData} updateFormData={updateFormData} />;
        case 2:
          return <PHBusinessMetricsStep formData={formData} updateFormData={updateFormData} />;
        case 3:
          return <PHTeamStep formData={formData} updateFormData={updateFormData} />;
        case 4:
          return <PHProjectStep formData={formData} updateFormData={updateFormData} />;
        case 5:
          return <CountryReviewStep formData={formData} updateFormData={updateFormData} onEdit={handleEditStep} onSubmit={handleSubmit} />;
        default:
          return null;
      }
    } else if (isMM) {
      switch (currentStep) {
        case 1:
          return <MMCompanyStep formData={formData} updateFormData={updateFormData} />;
        case 2:
          return <MMBusinessMetricsStep formData={formData} updateFormData={updateFormData} />;
        case 3:
          return <MMTeamStep formData={formData} updateFormData={updateFormData} />;
        case 4:
          return <MMProjectStep formData={formData} updateFormData={updateFormData} />;
        case 5:
          return <CountryReviewStep formData={formData} updateFormData={updateFormData} onEdit={handleEditStep} onSubmit={handleSubmit} />;
        default:
          return null;
      }
    } else if (isKH) {
      switch (currentStep) {
        case 1:
          return <KHCompanyStep formData={formData} updateFormData={updateFormData} />;
        case 2:
          return <KHBusinessMetricsStep formData={formData} updateFormData={updateFormData} />;
        case 3:
          return <KHTeamStep formData={formData} updateFormData={updateFormData} />;
        case 4:
          return <KHProjectStep formData={formData} updateFormData={updateFormData} />;
        case 5:
          return <CountryReviewStep formData={formData} updateFormData={updateFormData} onEdit={handleEditStep} onSubmit={handleSubmit} />;
        default:
          return null;
      }
    } else if (isLA) {
      switch (currentStep) {
        case 1:
          return <LACompanyStep formData={formData} updateFormData={updateFormData} />;
        case 2:
          return <LABusinessMetricsStep formData={formData} updateFormData={updateFormData} />;
        case 3:
          return <LATeamStep formData={formData} updateFormData={updateFormData} />;
        case 4:
          return <LAProjectStep formData={formData} updateFormData={updateFormData} />;
        case 5:
          return <CountryReviewStep formData={formData} updateFormData={updateFormData} onEdit={handleEditStep} onSubmit={handleSubmit} />;
        default:
          return null;
      }
    } else if (isBN) {
      switch (currentStep) {
        case 1:
          return <BNCompanyStep formData={formData} updateFormData={updateFormData} />;
        case 2:
          return <BNBusinessMetricsStep formData={formData} updateFormData={updateFormData} />;
        case 3:
          return <BNTeamStep formData={formData} updateFormData={updateFormData} />;
        case 4:
          return <BNProjectStep formData={formData} updateFormData={updateFormData} />;
        case 5:
          return <BNReviewStep formData={formData} updateFormData={updateFormData} onEdit={handleEditStep} onSubmit={handleSubmit} />;
        default:
          return null;
      }
    } else {
      // SG flow
      switch (currentStep) {
        case 1:
          if (sgConsolidatedSectionsEnabled) {
            const Header = ConsolidatedSectionHeader;
            return (
              <div className="space-y-8 sg-consolidated-form" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', fontWeight: 300 }}>
                <section className="space-y-4" aria-labelledby="step-company-heading">
                  <Header index={1} title="Company Details" sectionId="step-company" />
                  <CompanyStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />
                </section>
                <section className="space-y-4" aria-labelledby="step-metrics-heading">
                  <Header index={2} title="Business Metrics" sectionId="step-metrics" />
                  <BusinessMetricsStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />
                </section>
                <section className="space-y-4" aria-labelledby="step-ops-heading">
                  <Header index={3} title="Operational Presence" sectionId="step-ops" />
                  <LocationOpsStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />
                </section>
                <section className="space-y-4" aria-labelledby="step-history-heading">
                  <Header index={4} title="Grant History" sectionId="step-history" />
                  <GrantHistoryStep
                    key={grantHistoryDevKey}
                    formData={formData}
                    updateFormData={updateFormData}
                    errors={fieldErrors}
                  />
                </section>
                <section className="space-y-4" aria-labelledby="step-funding-strategy-heading">
                  <Header index={5} title="Funding Strategy" sectionId="step-funding-strategy" />
                  <FundingStrategyStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />
                </section>
                <section className="space-y-4" aria-labelledby="step-project-heading">
                  <Header index={6} title="Project Details" sectionId="step-project" />
                  <ProjectStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />
                </section>
                <section className="space-y-4" aria-labelledby="step-compliance-heading">
                  <Header index={7} title="Compliance" sectionId="step-compliance" />
                  <ComplianceStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />
                </section>
              </div>
            );
          }
          return <CompanyStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />;
        case 2:
          return <BusinessMetricsStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />;
        case 3:
          return <LocationOpsStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />;
        case 4:
          return (
            <GrantHistoryStep
              key={grantHistoryDevKey}
              formData={formData}
              updateFormData={updateFormData}
              errors={fieldErrors}
            />
          );
        case 5:
          return <FundingStrategyStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />;
        case 6:
          return <ProjectStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />;
        case 7:
          return <ComplianceStep formData={formData} updateFormData={updateFormData} errors={fieldErrors} />;
        case 8:
          if (sgSupportingDocsStepOn) {
            return (
              <SupportingDocumentsStep
                formData={formData}
                patchSupportingDocuments={patchSupportingDocuments}
              />
            );
          }
          return (
            <ReviewStep
              formData={formData}
              updateFormData={updateFormData}
              onSubmit={handleSubmit}
              onEdit={handleEditStep}
              supportingDocsStepEnabled={false}
              fundingStepNumber={5}
              projectStepNumber={6}
              complianceStepNumber={7}
            />
          );
        case 9:
          if (!sgSupportingDocsStepOn) return null;
          return (
            <ReviewStep
              formData={formData}
              updateFormData={updateFormData}
              onSubmit={handleSubmit}
              onEdit={handleEditStep}
              supportingDocsStepEnabled
              fundingStepNumber={5}
              projectStepNumber={6}
              complianceStepNumber={7}
              supportingDocsStepNumber={8}
            />
          );
        default:
          return null;
      }
    }
  };

  if (!embedded) {
    return <Navigate to="/dashboard/eligibility-assessment" state={{ clientId }} replace />;
  }

  if (showTierGate) {
    return (
      <TierLockedView
        requiredTier={requiredTier}
        signedIn={signedIn}
        redirectPath={location.pathname}
      />
    );
  }

  /** Summary of what still needs an answer: a list of links to the section / field. */
  const validationSummary =
    errorsShown && validationIssues.length > 0 ? (
      <div
        role="alert"
        id="checker-validation-summary"
        data-testid="checker-validation-summary"
        className="w-full max-w-2xl rounded-xl border px-4 py-3 text-left"
        style={{ borderColor: '#FECACA', backgroundColor: '#FEF2F2', color: '#B91C1C' }}
      >
        <p className="text-sm font-medium leading-relaxed">
          {validationIssues.length === 1
            ? `1 answer is still needed before you can ${sgConsolidatedSectionsEnabled ? 'review' : 'continue'}.`
            : `${validationIssues.length} answers are still needed before you can ${sgConsolidatedSectionsEnabled ? 'review' : 'continue'}.`}
        </p>
        <ul className="mt-2 space-y-1 text-sm">
          {validationIssues.map((issue) => {
            const sectionId = sectionForField(issue.field);
            return (
              <li key={issue.field}>
                <a
                  href={`#${sectionId}`}
                  className="underline underline-offset-2"
                  style={{ color: '#B91C1C' }}
                  onClick={(event) => {
                    event.preventDefault();
                    if (!focusInvalidField(issue.field)) scrollToSection(sectionId);
                  }}
                >
                  {issue.label}
                </a>
                <span style={{ color: '#7F1D1D' }}> — {SECTION_TITLES[sectionId] || 'Project Details'}</span>
              </li>
            );
          })}
        </ul>
      </div>
    ) : null;

  const draftNoticeBanner = draftNotice ? (
    <div
      role="status"
      data-testid="checker-draft-notice"
      className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm"
      style={{ borderColor: '#E5E7EB', backgroundColor: '#F9FAFB', color: '#4B5563' }}
    >
      <span>
        {draftNotice === 'prefill'
          ? 'Your previous answers have been filled in so you can try again.'
          : 'We restored the answers you had not submitted yet.'}
      </span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={startOver}
          className="underline underline-offset-2"
          style={{ color: '#1d4ed8' }}
        >
          Clear and start over
        </button>
        <button
          type="button"
          onClick={() => setDraftNotice('')}
          aria-label="Dismiss"
          className="inline-flex h-6 w-6 items-center justify-center"
          style={{ color: '#6B7280' }}
        >
          <span aria-hidden>×</span>
        </button>
      </div>
    </div>
  ) : null;

  /**
   * Fit-gate caution from the backend. It is a warning, not a block: the user
   * can go back to the project section or run the assessment anyway.
   */
  const fitGatePanel = fitGateWarning ? (
    <div
      role="alert"
      data-testid="checker-fit-gate-warning"
      className="mb-6 overflow-hidden rounded-lg border bg-white"
      style={{ borderColor: '#E5E7EB', color: '#374151' }}
    >
      <div className="flex">
        <div className="w-1.5 shrink-0" style={{ backgroundColor: '#F59E0B' }} aria-hidden />
        <div className="min-w-0 flex-1 px-4 py-3.5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: '#D97706' }} aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold" style={{ color: '#111827' }}>
                This project may not fit the Singapore SME grant scope
              </p>
              <div className="mt-1.5 space-y-1 text-sm leading-relaxed" style={{ color: '#4B5563' }}>
                {fitGateWarning.cautions.map((caution) => (
                  <p key={caution}>{caution}</p>
                ))}
                <p>
                  Refine the project type and description first, or run the assessment as it is. If you run it
                  anyway, weaker-fit schemes will be scored low rather than hidden.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 pl-7">
            <button
              type="button"
              onClick={() => handleEditSection('step-project')}
              className="rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors hover:bg-slate-50"
              style={{ borderColor: '#CBD5E1', color: '#374151', backgroundColor: '#FFFFFF' }}
            >
              Edit project
            </button>
            <button
              type="button"
              onClick={() => handleSubmit({ skipFitGate: true })}
              disabled={isSubmitting}
              className="rounded-full px-3.5 py-1.5 text-sm font-medium text-white transition-opacity disabled:opacity-60"
              style={{ backgroundColor: '#1D4ED8' }}
            >
              {isSubmitting ? 'Running assessment…' : 'Run assessment anyway'}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
  const useAssessmentHeroLayout = currentStep === 1 || isSingaporeEligibility;

  return (
    <div
      className={`checker w-full max-w-full overflow-x-clip${
        embedded ? '' : ' min-h-screen'
      }`}
    >
      {!embedded && (
        <SEO
          title="Eligibility assessment | OPTRA"
          description="Check your eligibility for Singapore grants instantly. OPTRA's AI-powered eligibility assessment analyzes your company profile and matches you with relevant grants."
          keywords="grant eligibility assessment, Singapore grants, SME funding, grant applications, eligibility assessment"
          url="/dashboard/eligibility-assessment"
        />
      )}
      {/* Header is now rendered at App level for proper sticky positioning */}
      <div
        className={`relative overflow-x-clip${embedded ? '' : ' min-h-screen'}`}
        style={{
          background: 'white',
          paddingTop: embedded ? 0 : 'var(--nav-gap, 16px)',
          isolation: 'isolate',
          willChange: 'contents',
          transform: 'translateZ(0)'
        }}
      >
      {/* Animated stars background behind the white form surface */}
      {!embedded && (
      <ClientOnly>
        <StarsBackground 
          starDensity={0.00012}
          allStarsTwinkle={true}
          twinkleProbability={0.7}
          minTwinkleSpeed={0.5}
          maxTwinkleSpeed={1}
          starColor="#60A5FA"
        />
        <ShootingStars
          minSpeed={10}
          maxSpeed={30}
          minDelay={2200}
          maxDelay={4200}
          starColor="#60A5FA"
          trailColor="#93C5FD"
          starWidth={30}
          starHeight={4}
        />
      </ClientOnly>
      )}

      {/* Hero title: all assessment steps. Hidden once results are shown. */}
      {!results ? (
      <div
        className={`relative z-[20] ${
          embedded
            ? (useAssessmentHeroLayout ? 'pt-4 pb-10' : 'pt-4 pb-6')
            : (useAssessmentHeroLayout ? 'pt-20 pb-24' : 'pt-16 md:pt-20 pb-8 md:pb-10')
        }`}
      >
        <Container
          className={useAssessmentHeroLayout ? 'text-left' : 'text-center'}
          style={{ textAlign: useAssessmentHeroLayout ? 'left' : 'center' }}
        >
          <div
            className={useAssessmentHeroLayout ? 'text-left animate-fade-in-up mb-6' : 'text-center animate-fade-in-up mb-6'}
            style={{ textAlign: useAssessmentHeroLayout ? 'left' : 'center' }}
          >
            <h1
              className={`text-emphasis ${
                embedded ? 'text-[30px] leading-tight' : 'text-5xl md:text-6xl lg:text-[3.75rem] leading-[1.08]'
              } mb-3 ${useAssessmentHeroLayout ? 'text-left' : 'text-center'}`}
              style={{
                letterSpacing: '-0.022em',
                color: '#60A5FA',
                margin: 0,
                marginBottom: embedded ? '1rem' : '1.75rem',
                fontWeight: 400,
                textAlign: useAssessmentHeroLayout ? 'left' : 'center',
              }}
            >
              {isMY ? 'Malaysia Grant Eligibility Assessment' : isID ? 'Indonesia Grant Eligibility Assessment' : isTH ? 'Thailand Grant Eligibility Assessment' : isVN ? 'Vietnam Grant Eligibility Assessment' : isPH ? 'Philippines Grant Eligibility Assessment' : isMM ? 'Myanmar Grant Eligibility Assessment' : isKH ? 'Cambodia Grant Eligibility Assessment' : isLA ? 'Laos Grant Eligibility Assessment' : isBN ? 'Brunei Grant Eligibility Assessment' : 'Eligibility assessment'}
            </h1>
            {currentStep === 1 || isSingaporeEligibility ? (
              <p
                className={
                  (isMY || isID || isTH || isVN || isPH || isMM || isKH || isLA || isBN) && !isSingaporeEligibility
                    ? 'text-xl mb-8 max-w-3xl text-left'
                    : 'text-sm mb-8 max-w-3xl text-left leading-relaxed'
                }
                style={{
                  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  fontWeight: 300,
                  color:
                    (isMY || isID || isTH || isVN || isPH || isMM || isKH || isLA || isBN) && !isSingaporeEligibility
                      ? '#4B5563'
                      : '#6B7280',
                  textAlign: 'left',
                }}
              >
                {isMY
                  ? 'Discover which Malaysian government grants your business qualifies for with AI-powered eligibility assessment.'
                  : isID
                  ? 'Discover which Indonesian government grants your business qualifies for with AI-powered eligibility assessment.'
                  : isTH
                  ? 'Discover which Thai government grants your business qualifies for with AI-powered eligibility assessment.'
                  : isVN
                  ? 'Discover which Vietnamese government grants your business qualifies for with AI-powered eligibility assessment.'
                  : isPH
                  ? 'Discover which Philippine government grants your business qualifies for with AI-powered eligibility assessment.'
                  : isMM
                  ? 'Discover which Myanmar government grants your business qualifies for with AI-powered eligibility assessment.'
                  : isKH
                  ? 'Discover which Cambodian government grants your business qualifies for with AI-powered eligibility assessment.'
                  : isLA
                  ? 'Discover which Lao government grants your business qualifies for with AI-powered eligibility assessment.'
                  : isBN
                  ? 'Discover which Brunei government grants your business qualifies for with AI-powered eligibility assessment.'
                  : 'Answer the seven sections below, review your answers, then run the assessment. By continuing, you agree that the information you submit may be shared with OPTRA Labs for assessment processing.'}
              </p>
            ) : (
              <p
                className="text-sm md:text-base max-w-2xl leading-relaxed text-center mx-auto"
                style={{ color: '#4B5563', textAlign: 'center' }}
              >
                {isMY || isID || isTH || isVN || isPH || isMM || isKH || isLA || isBN
                  ? 'Upon submission, your responses are assessed against relevant programme criteria. You receive a structured summary of fit and recommended next steps to support prioritisation and application preparation.'
                  : 'Your answers are assessed against typical Singapore grant criteria. You receive a structured summary of fit, priorities, and gaps to guide programme selection and preparation.'}
              </p>
            )}
          </div>

          {useAssessmentHeroLayout ? (
              <div className="flex flex-wrap gap-4 text-sm justify-start">
                {ELIGIBILITY_HERO_PILLS.map((tag) => (
                  <span
                    key={tag.label}
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm border"
                    style={heroPillStyle}
                  >
                    <tag.icon className="w-4 h-4" />
                    {tag.label}
                  </span>
                ))}
            </div>
          ) : null}
        </Container>
      </div>
      ) : null}

      {embedded && isSingaporeEligibility && !results ? (
        <Container className="pb-4 md:pb-6">
          <div className="h-px w-full" data-cw="intro-rule" style={{ backgroundColor: '#E5E7EB' }} />
        </Container>
      ) : null}

      {/* Stepper (outside the card for the other country flows; the Singapore flow renders it inside the card, as the US wizard does) */}
      {!isSingaporeEligibility && (!sgConsolidatedSectionsEnabled || (currentStep !== 1 && currentStep !== 8)) ? (
        <StepperProgress 
          currentStep={currentStep} 
          completedSteps={completedSteps} 
          totalSteps={maxSteps}
          onStepClick={(stepId) => {
            // Allow navigation to completed steps, current step, or next step if current is completed
            const maxCompleted = completedSteps.length > 0 ? Math.max(...completedSteps) : 0;
            const maxAllowed = Math.max(maxCompleted, currentStep);
            if (completedSteps.includes(stepId) || stepId === currentStep || (stepId === currentStep + 1 && completedSteps.includes(currentStep)) || stepId <= maxAllowed) {
              setCurrentStep(stepId);
              scrollToFormTop();
            }
          }}
        />
      ) : null}

      {/* Container does not forward id / data-testid, so the anchors live on the inner wrapper:
          `#wizard-start` is the scroll target and `[data-testid="checker-card"]` keys the theme. */}
      <Container className={`pt-4 md:pt-12 ${embedded ? 'pb-12' : 'pb-48'}`}>
        <div className="w-full" id="wizard-start" data-testid="checker-card">
          {sgConsolidatedSectionsEnabled && currentStep === 1 ? (
            <div className="checker-form-ui checker-flat-shell p-0 md:p-0" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
              {localDevDemoPanel}
              {draftNoticeBanner}
              {renderStep()}
              <div className="bg-white/5 backdrop-blur-xl px-8 py-6 border-t border-white/10 mt-6">
                <div className="flex justify-between items-start gap-6 flex-wrap">
                  <div className="min-w-0 flex-1">{validationSummary}</div>
                  <div className="ml-auto flex max-w-[32rem] flex-col items-end gap-2 text-right">
                    {validationError && (
                      <p className="text-sm font-medium leading-relaxed text-red-400" role="alert">
                        {validationError}
                      </p>
                    )}
                    <CTAButton
                      onClick={handleConsolidatedContinue}
                    >
                      Review answers
                    </CTAButton>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <GlassPanel className="p-6 md:p-8 overflow-visible relative">
              <div className="checker-form-ui" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
              {isSingaporeEligibility && !sgConsolidatedSectionsEnabled ? (
                <StepperProgress
                  currentStep={currentStep}
                  completedSteps={completedSteps}
                  totalSteps={maxSteps}
                  onStepClick={(stepId) => {
                    setCurrentStep(stepId);
                    scrollToFormTop();
                  }}
                />
              ) : null}
              {isSingaporeEligibility && !sgConsolidatedSectionsEnabled ? (
                <div className="cw-progress" data-cw="progress">
                  <div
                    className="cw-progress__track"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={getProgress(currentStep, false, maxSteps)}
                    aria-label="Assessment progress"
                  >
                    <div className="cw-progress__fill" style={{ width: `${getProgress(currentStep, false, maxSteps)}%` }} />
                  </div>
                  <span className="cw-progress__label">{getProgress(currentStep, false, maxSteps)}% complete</span>
                </div>
              ) : null}
              {localDevDemoPanel}
              {draftNoticeBanner}
              {isSingaporeEligibility && !sgConsolidatedSectionsEnabled ? (
                <p className="cw-kicker" data-cw="kicker">Step {currentStep} of {maxSteps}</p>
              ) : null}
              <div className="cw-step" data-cw="step">
                {renderStep()}
              </div>

              {fitGatePanel}

              {/* Navigation */}
              <div className="bg-white/5 backdrop-blur-xl px-8 py-6 border-t border-white/10 mt-6" data-cw="nav">
                <div className="flex justify-between items-center gap-6 flex-wrap">
                  {currentStep > 1 ? (
                    <div data-cw="prev"><CTAButton
                      onClick={
                        sgConsolidatedSectionsEnabled && currentStep === 8
                          ? () => {
                              setValidationError('');
                              setFitGateWarning(null);
                              setCurrentStep(1);
                            }
                          : handlePrevious
                      }
                    >
                      Previous
                    </CTAButton></div>
                  ) : (
                    <div></div>
                  )}

                  <div
                    className="text-sm font-semibold px-4 py-2 rounded-full"
                    data-cw="pill"
                    style={{ ...heroPillStyle, paddingRight: '1.25rem', paddingLeft: '1.25rem' }}
                  >
                    {sgConsolidatedSectionsEnabled && currentStep === maxSteps
                      ? 'Review'
                      : `Step ${currentStep} of ${maxSteps}`}
                  </div>

                  {currentStep < maxSteps ? (
                    // Always enabled: on an incomplete step, handleNext lists the missing answers
                    // and marks the fields instead of leaving a dead button (as the US wizard does).
                    <div data-cw="next"><CTAButton onClick={handleNext}>
                      Next
                    </CTAButton></div>
                  ) : (
                    <div data-cw="submit"><CTAButton
                      onClick={() => handleSubmit()}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Running assessment…' : 'Run assessment'}
                    </CTAButton></div>
                  )}
                </div>

                {/* Validation messages */}
                {validationSummary ? <div className="mt-4">{validationSummary}</div> : null}
                {validationError && (
                  <p className="mt-4 text-sm font-medium leading-relaxed text-red-300" role="alert" data-cw="alert">
                    {validationError}
                  </p>
                )}
              </div>
              <BorderBeam
                duration={6}
                size={400}
                className="from-transparent via-[#60A5FA] to-transparent opacity-60"
              />
              <BorderBeam
                duration={6}
                delay={3}
                size={400}
                borderWidth={2}
                className="from-transparent via-[#60A5FA] to-transparent opacity-40"
              />
              </div>
            </GlassPanel>
          )}
        </div>
      </Container>
      
      {!embedded && <EnhancedFooter />}
      </div>
    </div>
  );
}
